export const REVIEW_IMAGE_ROTATION_MIN = -45;
export const REVIEW_IMAGE_ROTATION_MAX = 45;
export const REVIEW_IMAGE_ROTATION_STEP = 0.25;
export const REVIEW_IMAGE_ROTATION_NUDGE = 0.5;
export const REVIEW_IMAGE_QUARTER_TURN = 90;
const REVIEW_IMAGE_ANALYSIS_MAX_DIMENSION = 512;
const REVIEW_IMAGE_ANALYSIS_BACKGROUND_MARGIN = 0.015;
const REVIEW_IMAGE_ANALYSIS_MIN_FOREGROUND_DISTANCE = 30;
const REVIEW_IMAGE_ANALYSIS_FOREGROUND_PADDING = 16;
const REVIEW_IMAGE_ANALYSIS_MIN_FOREGROUND_RUN = 3;
const REVIEW_IMAGE_ANALYSIS_SCAN_INSET = 0.08;
const REVIEW_IMAGE_ANALYSIS_SIDE_FRACTION = 0.48;
const REVIEW_IMAGE_ANALYSIS_MIN_POINTS = 32;
const REVIEW_IMAGE_ANALYSIS_INLIER_DISTANCE = 2.5;
const REVIEW_IMAGE_ANALYSIS_MIN_COVERAGE = 0.28;
const REVIEW_IMAGE_ANALYSIS_MAX_RMS = 2;
const REVIEW_IMAGE_ANALYSIS_MAX_SIDE_DISAGREEMENT = 1.5;
const REVIEW_IMAGE_ANALYSIS_MAX_PAIR_DISAGREEMENT = 1;

export function clampReviewImageRotation(degrees: number): number {
  if (!Number.isFinite(degrees)) return 0;
  return Math.min(REVIEW_IMAGE_ROTATION_MAX, Math.max(REVIEW_IMAGE_ROTATION_MIN, degrees));
}

export function normalizeReviewImageRotation(degrees: number): number {
  if (!Number.isFinite(degrees)) return 0;
  const normalized = ((((degrees + 180) % 360) + 360) % 360) - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export interface ReviewImageRotationParts {
  quarterTurnDegrees: -180 | -90 | 0 | 90 | 180;
  fineDegrees: number;
}

export function reviewImageRotationParts(degrees: number): ReviewImageRotationParts {
  const normalized = normalizeReviewImageRotation(degrees);
  let quarterTurnDegrees: ReviewImageRotationParts['quarterTurnDegrees'];
  if (normalized >= -45 && normalized <= 45) quarterTurnDegrees = 0;
  else if (normalized > 45 && normalized <= 135) quarterTurnDegrees = 90;
  else if (normalized < -45 && normalized >= -135) quarterTurnDegrees = -90;
  else quarterTurnDegrees = normalized > 0 ? 180 : -180;
  return {
    quarterTurnDegrees,
    fineDegrees: normalized - quarterTurnDegrees,
  };
}

// Match object-fit: contain after a quarter turn, without shrinking square sources.
// Fine straightening keeps its existing scale and cropping behavior.
export function reviewImageQuarterTurnScale(
  width: number,
  height: number,
  frameWidth: number,
  frameHeight: number,
  degrees: number,
): number {
  if (
    ![width, height, frameWidth, frameHeight].every((value) => Number.isFinite(value) && value > 0)
  ) {
    return 1;
  }
  if (
    Math.abs(reviewImageRotationParts(degrees).quarterTurnDegrees) !== REVIEW_IMAGE_QUARTER_TURN
  ) {
    return 1;
  }
  const originalFit = Math.min(frameWidth / width, frameHeight / height);
  const rotatedFit = Math.min(frameWidth / height, frameHeight / width);
  return rotatedFit / originalFit;
}

export interface ReviewImageRotationGeometry {
  canvasWidth: number;
  canvasHeight: number;
  radians: number;
  drawX: number;
  drawY: number;
  drawWidth: number;
  drawHeight: number;
}

export interface ReviewImagePixels {
  data: Uint8ClampedArray | Uint8Array;
  width: number;
  height: number;
}

export interface ReviewImageSkewEstimate {
  correctionDegrees: number;
  detectedDegrees: number;
  confidence: number;
  edgeSamples: number;
}

export function reviewImageRotationGeometry(
  width: number,
  height: number,
  degrees: number,
): ReviewImageRotationGeometry {
  const normalized = normalizeReviewImageRotation(degrees);
  const { quarterTurnDegrees } = reviewImageRotationParts(normalized);
  const swapsDimensions = Math.abs(quarterTurnDegrees) === REVIEW_IMAGE_QUARTER_TURN;
  return {
    canvasWidth: swapsDimensions ? height : width,
    canvasHeight: swapsDimensions ? width : height,
    radians: (normalized * Math.PI) / 180,
    drawX: -width / 2,
    drawY: -height / 2,
    drawWidth: width,
    drawHeight: height,
  };
}

interface Point {
  independent: number;
  dependent: number;
}

interface LineFit {
  slope: number;
  intercept: number;
  inliers: number;
  coverage: number;
  rms: number;
}

interface AnglePair {
  degrees: number;
  confidence: number;
  inliers: number;
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? 0;
  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}

function pixelColorDistance(
  pixels: ReviewImagePixels,
  pixel: number,
  background: readonly [number, number, number],
): number {
  const offset = pixel * 4;
  return (
    Math.abs((pixels.data[offset] ?? 0) - background[0]) +
    Math.abs((pixels.data[offset + 1] ?? 0) - background[1]) +
    Math.abs((pixels.data[offset + 2] ?? 0) - background[2])
  );
}

function foregroundMask(pixels: ReviewImagePixels): Uint8Array {
  const margin = Math.max(
    2,
    Math.round(Math.min(pixels.width, pixels.height) * REVIEW_IMAGE_ANALYSIS_BACKGROUND_MARGIN),
  );
  const red: number[] = [];
  const green: number[] = [];
  const blue: number[] = [];
  const isBackgroundSample = (x: number, y: number) =>
    x < margin || x >= pixels.width - margin || y < margin || y >= pixels.height - margin;
  for (let y = 0; y < pixels.height; y += 1) {
    for (let x = 0; x < pixels.width; x += 1) {
      if (!isBackgroundSample(x, y)) continue;
      const offset = (y * pixels.width + x) * 4;
      red.push(pixels.data[offset] ?? 0);
      green.push(pixels.data[offset + 1] ?? 0);
      blue.push(pixels.data[offset + 2] ?? 0);
    }
  }
  const background = [median(red), median(green), median(blue)] as const;
  const sampleDistances: number[] = [];
  for (let y = 0; y < pixels.height; y += 1) {
    for (let x = 0; x < pixels.width; x += 1) {
      if (isBackgroundSample(x, y)) {
        sampleDistances.push(pixelColorDistance(pixels, y * pixels.width + x, background));
      }
    }
  }
  sampleDistances.sort((a, b) => a - b);
  const percentileIndex = Math.min(
    sampleDistances.length - 1,
    Math.floor(sampleDistances.length * 0.95),
  );
  const threshold = Math.max(
    REVIEW_IMAGE_ANALYSIS_MIN_FOREGROUND_DISTANCE,
    (sampleDistances[percentileIndex] ?? 0) + REVIEW_IMAGE_ANALYSIS_FOREGROUND_PADDING,
  );
  const mask = new Uint8Array(pixels.width * pixels.height);
  for (let pixel = 0; pixel < mask.length; pixel += 1) {
    mask[pixel] = pixelColorDistance(pixels, pixel, background) >= threshold ? 1 : 0;
  }
  return mask;
}

function firstForegroundRun(
  mask: Uint8Array,
  width: number,
  fixed: number,
  start: number,
  end: number,
  step: 1 | -1,
  horizontal: boolean,
): number | null {
  let run = 0;
  for (let position = start; step > 0 ? position <= end : position >= end; position += step) {
    const pixel = horizontal ? fixed * width + position : position * width + fixed;
    if ((mask[pixel] ?? 0) === 1) {
      run += 1;
      if (run >= REVIEW_IMAGE_ANALYSIS_MIN_FOREGROUND_RUN) {
        return position - step * (REVIEW_IMAGE_ANALYSIS_MIN_FOREGROUND_RUN - 1);
      }
    } else {
      run = 0;
    }
  }
  return null;
}

function linearRegression(points: readonly Point[]): { slope: number; intercept: number } | null {
  if (points.length < 2) return null;
  let sumIndependent = 0;
  let sumDependent = 0;
  let sumSquare = 0;
  let sumProduct = 0;
  for (const point of points) {
    sumIndependent += point.independent;
    sumDependent += point.dependent;
    sumSquare += point.independent * point.independent;
    sumProduct += point.independent * point.dependent;
  }
  const denominator = points.length * sumSquare - sumIndependent * sumIndependent;
  if (Math.abs(denominator) < Number.EPSILON) return null;
  const slope = (points.length * sumProduct - sumIndependent * sumDependent) / denominator;
  return {
    slope,
    intercept: (sumDependent - slope * sumIndependent) / points.length,
  };
}

function fitContourLine(points: readonly Point[], scanCount: number): LineFit | null {
  if (points.length < REVIEW_IMAGE_ANALYSIS_MIN_POINTS) return null;
  const sorted = [...points].sort((a, b) => a.independent - b.independent);
  const anchorCount = Math.min(14, Math.max(4, Math.floor(sorted.length / 8)));
  const low = sorted.slice(0, Math.ceil(sorted.length * 0.3));
  const high = sorted.slice(Math.floor(sorted.length * 0.7));
  let bestInliers: Point[] = [];
  let bestResidual = Number.POSITIVE_INFINITY;

  for (let lowIndex = 0; lowIndex < anchorCount; lowIndex += 1) {
    const start =
      low[Math.round((lowIndex * Math.max(0, low.length - 1)) / Math.max(1, anchorCount - 1))];
    if (start === undefined) continue;
    for (let highIndex = 0; highIndex < anchorCount; highIndex += 1) {
      const finish =
        high[Math.round((highIndex * Math.max(0, high.length - 1)) / Math.max(1, anchorCount - 1))];
      if (finish === undefined || finish.independent === start.independent) continue;
      const slope = (finish.dependent - start.dependent) / (finish.independent - start.independent);
      const intercept = start.dependent - slope * start.independent;
      const inliers: Point[] = [];
      let residual = 0;
      for (const point of sorted) {
        const distance = Math.abs(point.dependent - (slope * point.independent + intercept));
        if (distance <= REVIEW_IMAGE_ANALYSIS_INLIER_DISTANCE) {
          inliers.push(point);
          residual += distance;
        }
      }
      if (
        inliers.length > bestInliers.length ||
        (inliers.length === bestInliers.length && residual < bestResidual)
      ) {
        bestInliers = inliers;
        bestResidual = residual;
      }
    }
  }

  const refined = linearRegression(bestInliers);
  if (refined === null) return null;
  const finalInliers = sorted.filter(
    (point) =>
      Math.abs(point.dependent - (refined.slope * point.independent + refined.intercept)) <=
      REVIEW_IMAGE_ANALYSIS_INLIER_DISTANCE,
  );
  const final = linearRegression(finalInliers);
  if (final === null) return null;
  const rms = Math.sqrt(
    finalInliers.reduce((total, point) => {
      const residual = point.dependent - (final.slope * point.independent + final.intercept);
      return total + residual * residual;
    }, 0) / finalInliers.length,
  );
  const coverage = finalInliers.length / Math.max(1, scanCount);
  if (
    finalInliers.length < REVIEW_IMAGE_ANALYSIS_MIN_POINTS ||
    coverage < REVIEW_IMAGE_ANALYSIS_MIN_COVERAGE ||
    rms > REVIEW_IMAGE_ANALYSIS_MAX_RMS
  ) {
    return null;
  }
  return {
    ...final,
    inliers: finalInliers.length,
    coverage,
    rms,
  };
}

function pairedAngle(
  first: LineFit | null,
  second: LineFit | null,
  vertical: boolean,
  independentCenter: number,
  dependentSize: number,
): AnglePair | null {
  if (first === null || second === null) return null;
  const separation = Math.abs(
    second.slope * independentCenter +
      second.intercept -
      (first.slope * independentCenter + first.intercept),
  );
  if (separation < dependentSize * 0.35) return null;
  const firstDegrees = ((Math.atan(first.slope) * 180) / Math.PI) * (vertical ? -1 : 1);
  const secondDegrees = ((Math.atan(second.slope) * 180) / Math.PI) * (vertical ? -1 : 1);
  if (Math.abs(firstDegrees - secondDegrees) > REVIEW_IMAGE_ANALYSIS_MAX_SIDE_DISAGREEMENT) {
    return null;
  }
  const firstWeight = first.coverage / Math.max(0.5, first.rms);
  const secondWeight = second.coverage / Math.max(0.5, second.rms);
  const degrees =
    (firstDegrees * firstWeight + secondDegrees * secondWeight) / (firstWeight + secondWeight);
  const agreement =
    1 - Math.abs(firstDegrees - secondDegrees) / REVIEW_IMAGE_ANALYSIS_MAX_SIDE_DISAGREEMENT;
  return {
    degrees,
    confidence:
      Math.min(first.coverage, second.coverage) *
      Math.max(0, agreement) *
      Math.exp(-(first.rms + second.rms) / 4),
    inliers: first.inliers + second.inliers,
  };
}

export function estimateReviewImageSkew(pixels: ReviewImagePixels): ReviewImageSkewEstimate {
  if (pixels.width < 3 || pixels.height < 3) throw new Error('image_dimensions_invalid');
  if (pixels.data.length < pixels.width * pixels.height * 4) {
    throw new Error('image_pixels_invalid');
  }

  const mask = foregroundMask(pixels);
  const insetX = Math.max(1, Math.round(pixels.width * REVIEW_IMAGE_ANALYSIS_SCAN_INSET));
  const insetY = Math.max(1, Math.round(pixels.height * REVIEW_IMAGE_ANALYSIS_SCAN_INSET));
  const maxLeft = Math.floor(pixels.width * REVIEW_IMAGE_ANALYSIS_SIDE_FRACTION);
  const minRight = Math.ceil(pixels.width * (1 - REVIEW_IMAGE_ANALYSIS_SIDE_FRACTION));
  const maxTop = Math.floor(pixels.height * REVIEW_IMAGE_ANALYSIS_SIDE_FRACTION);
  const minBottom = Math.ceil(pixels.height * (1 - REVIEW_IMAGE_ANALYSIS_SIDE_FRACTION));
  const left: Point[] = [];
  const right: Point[] = [];
  const top: Point[] = [];
  const bottom: Point[] = [];

  for (let y = insetY; y < pixels.height - insetY; y += 1) {
    const leftX = firstForegroundRun(mask, pixels.width, y, 1, maxLeft, 1, true);
    const rightX = firstForegroundRun(mask, pixels.width, y, pixels.width - 2, minRight, -1, true);
    if (leftX !== null) left.push({ independent: y, dependent: leftX });
    if (rightX !== null) right.push({ independent: y, dependent: rightX });
  }
  for (let x = insetX; x < pixels.width - insetX; x += 1) {
    const topY = firstForegroundRun(mask, pixels.width, x, 1, maxTop, 1, false);
    const bottomY = firstForegroundRun(
      mask,
      pixels.width,
      x,
      pixels.height - 2,
      minBottom,
      -1,
      false,
    );
    if (topY !== null) top.push({ independent: x, dependent: topY });
    if (bottomY !== null) bottom.push({ independent: x, dependent: bottomY });
  }

  const verticalScanCount = Math.max(1, pixels.height - insetY * 2);
  const horizontalScanCount = Math.max(1, pixels.width - insetX * 2);
  const verticalPair = pairedAngle(
    fitContourLine(left, verticalScanCount),
    fitContourLine(right, verticalScanCount),
    true,
    pixels.height / 2,
    pixels.width,
  );
  const horizontalPair = pairedAngle(
    fitContourLine(top, horizontalScanCount),
    fitContourLine(bottom, horizontalScanCount),
    false,
    pixels.width / 2,
    pixels.height,
  );
  if (verticalPair === null && horizontalPair === null) {
    throw new Error('image_skew_uncertain');
  }
  if (
    verticalPair !== null &&
    horizontalPair !== null &&
    Math.abs(verticalPair.degrees - horizontalPair.degrees) >
      REVIEW_IMAGE_ANALYSIS_MAX_PAIR_DISAGREEMENT
  ) {
    throw new Error('image_skew_uncertain');
  }
  const pairs = [verticalPair, horizontalPair].filter((pair): pair is AnglePair => pair !== null);
  const totalConfidence = pairs.reduce((total, pair) => total + pair.confidence, 0);
  if (totalConfidence <= Number.EPSILON) throw new Error('image_skew_uncertain');
  const detectedDegrees =
    pairs.reduce((total, pair) => total + pair.degrees * pair.confidence, 0) / totalConfidence;
  const confidence = Math.min(1, totalConfidence / pairs.length);
  const edgeSamples = pairs.reduce((total, pair) => total + pair.inliers, 0);
  const correctionDegrees = clampReviewImageRotation(
    Math.round(-detectedDegrees / REVIEW_IMAGE_ROTATION_STEP) * REVIEW_IMAGE_ROTATION_STEP,
  );
  return {
    correctionDegrees:
      Math.abs(correctionDegrees) < REVIEW_IMAGE_ROTATION_STEP ? 0 : correctionDegrees,
    detectedDegrees,
    confidence,
    edgeSamples,
  };
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('image_decode_failed'));
    };
    image.src = objectUrl;
  });
}

export async function estimateReviewImageRotation(
  sourceUrl: string,
): Promise<ReviewImageSkewEstimate> {
  const response = await fetch(sourceUrl);
  if (!response.ok) throw new Error(`image_download_failed_${response.status}`);
  const image = await loadImage(await response.blob());
  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
    throw new Error('image_dimensions_invalid');
  }
  const scale = Math.min(
    1,
    REVIEW_IMAGE_ANALYSIS_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const width = Math.max(3, Math.round(image.naturalWidth * scale));
  const height = Math.max(3, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) throw new Error('image_canvas_unavailable');
  context.drawImage(image, 0, 0, width, height);
  return estimateReviewImageSkew(context.getImageData(0, 0, width, height));
}

export async function rotateReviewImageToJpeg(sourceUrl: string, degrees: number): Promise<Blob> {
  const response = await fetch(sourceUrl);
  if (!response.ok) throw new Error(`image_download_failed_${response.status}`);
  const image = await loadImage(await response.blob());
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (width <= 0 || height <= 0) throw new Error('image_dimensions_invalid');

  const geometry = reviewImageRotationGeometry(width, height, degrees);
  const canvas = document.createElement('canvas');
  canvas.width = geometry.canvasWidth;
  canvas.height = geometry.canvasHeight;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('image_canvas_unavailable');

  context.fillStyle = '#000000';
  context.fillRect(0, 0, geometry.canvasWidth, geometry.canvasHeight);
  context.translate(geometry.canvasWidth / 2, geometry.canvasHeight / 2);
  context.rotate(geometry.radians);
  context.drawImage(image, geometry.drawX, geometry.drawY, geometry.drawWidth, geometry.drawHeight);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) reject(new Error('image_encode_failed'));
        else resolve(blob);
      },
      'image/jpeg',
      0.95,
    );
  });
}
